import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { HelpCircle, Send, Eye, Clock, Book } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Link } from 'react-router-dom';

export default function Support() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [formData, setFormData] = useState({
    subject: '',
    category: '',
    priority: 'medium',
    description: '',
  });

  useEffect(() => {
    fetchMyTickets();
  }, []);

  const fetchMyTickets = async () => {
    setLoadingTickets(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.get(`${backendUrl}/api/tickets/my-tickets`);
      setTickets(res.data.tickets || []);
    } catch (error) {
      console.error('Erreur chargement tickets:', error);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation côté client
    if (!formData.subject.trim() || formData.subject.trim().length < 5) {
      toast.error('Le sujet doit contenir au moins 5 caractères');
      return;
    }
    
    if (!formData.description.trim() || formData.description.trim().length < 10) {
      toast.error('La description doit contenir au moins 10 caractères');
      return;
    }
    
    if (!formData.category) {
      toast.error('Veuillez sélectionner une catégorie');
      return;
    }

    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${backendUrl}/api/tickets`, formData);
      
      toast.success('Votre demande a été envoyée avec succès !');
      setFormData({
        subject: '',
        category: '',
        priority: 'medium',
        description: '',
      });
      // Refresh tickets list
      fetchMyTickets();
    } catch (error) {
      console.error('Erreur envoi ticket:', error);
      
      // Gérer les erreurs de validation Pydantic
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Erreur de validation Pydantic - extraire les messages
          const errorMessages = detail.map(err => {
            const field = err.loc?.join('.') || 'Champ';
            return `${field}: ${err.msg || 'Erreur de validation'}`;
          }).join(', ');
          toast.error(`Erreur de validation : ${errorMessages}`);
        } else if (typeof detail === 'string') {
          toast.error(detail);
        } else {
          toast.error('Erreur lors de l\'envoi de la demande');
        }
      } else {
        toast.error('Erreur lors de l\'envoi de la demande');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { label: 'Ouvert', variant: 'secondary' },
      in_progress: { label: 'En cours', variant: 'default' },
      awaiting_user: { label: 'Réponse requise', variant: 'destructive' },
      resolved: { label: 'Résolu', variant: 'outline' },
      closed: { label: 'Fermé', variant: 'outline' },
      auto_closed: { label: 'Fermé auto', variant: 'secondary' }
    };
    const config = statusConfig[status] || { label: status, variant: 'secondary' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { label: 'Basse', variant: 'secondary' },
      medium: { label: 'Moyenne', variant: 'default' },
      high: { label: 'Haute', variant: 'destructive' },
      urgent: { label: 'Urgente', variant: 'destructive' }
    };
    const config = priorityConfig[priority] || { label: priority, variant: 'secondary' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };


  return (
    <div className="container-custom py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
          <HelpCircle className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Besoin d'aide ?</h1>
          <p className="text-muted-foreground">Contactez notre équipe support</p>
        </div>
      </div>

      {/* FAQ Banner */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <Book className="h-8 w-8 text-primary" />
            <div>
              <h3 className="font-semibold">Consultez d'abord notre FAQ</h3>
              <p className="text-sm text-muted-foreground">
                Trouvez rapidement des réponses aux questions fréquentes
              </p>
            </div>
          </div>
          <Link to="/faq">
            <Button variant="outline" size="sm">
              Voir la FAQ
            </Button>
          </Link>
        </CardContent>
      </Card>

      {/* Support Form */}
      <Card>
        <CardHeader>
          <CardTitle>Créer une demande de support</CardTitle>
          <CardDescription>
            Décrivez votre problème et notre équipe vous répondra dans les plus brefs délais.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject">
                Sujet <span className="text-destructive">*</span>
              </Label>
              <Input
                id="subject"
                placeholder="Résumé de votre problème (minimum 5 caractères)"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                maxLength={200}
                minLength={5}
                required
                data-testid="ticket-subject"
              />
              <p className="text-xs text-muted-foreground">
                {formData.subject.length}/200 caractères (minimum 5)
              </p>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category">
                Catégorie <span className="text-destructive">*</span>
              </Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({ ...formData, category: v })}
                required
              >
                <SelectTrigger id="category" data-testid="ticket-category">
                  <SelectValue placeholder="Sélectionnez une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="account_access">Problème d'accès au compte</SelectItem>
                  <SelectItem value="payment">Question sur les paiements</SelectItem>
                  <SelectItem value="contract">Problème avec un contrat</SelectItem>
                  <SelectItem value="technical">Problème technique</SelectItem>
                  <SelectItem value="other">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <Label htmlFor="priority">Priorité</Label>
              <Select 
                value={formData.priority} 
                onValueChange={(v) => setFormData({ ...formData, priority: v })}
              >
                <SelectTrigger id="priority" data-testid="ticket-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Basse (réponse sous 48h)</SelectItem>
                  <SelectItem value="medium">Moyenne (réponse sous 24h)</SelectItem>
                  <SelectItem value="high">Haute (réponse sous 12h)</SelectItem>
                  <SelectItem value="urgent">Urgente (réponse immédiate)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">
                Description <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="description"
                placeholder="Décrivez votre problème en détail (minimum 10 caractères)..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={8}
                maxLength={2000}
                minLength={10}
                required
                data-testid="ticket-description"
              />
              <p className="text-xs text-muted-foreground">
                {formData.description.length}/2000 caractères (minimum 10)
              </p>
            </div>

            {/* User Info */}
            <div className="bg-muted/50 p-4 rounded-lg space-y-2">
              <p className="text-sm font-semibold">Vos informations</p>
              <p className="text-sm text-muted-foreground">
                Email : {user?.email}
              </p>
              <p className="text-xs text-muted-foreground">
                Notre équipe pourra vous contacter à cette adresse
              </p>
            </div>

            {/* Submit */}
            <div className="flex gap-4">
              <Button 
                type="submit" 
                disabled={loading}
                className="flex-1"
                data-testid="submit-ticket"
              >
                {loading ? (
                  'Envoi en cours...'
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Envoyer la demande
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Help Info */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <h3 className="font-semibold">Délais de réponse</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium">Priorité Basse/Moyenne</p>
                <p className="text-muted-foreground">Réponse sous 24-48h ouvrées</p>
              </div>
              <div>
                <p className="font-medium">Priorité Haute/Urgente</p>
                <p className="text-muted-foreground">Réponse sous 2-12h</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* My Tickets Section */}
      <Separator className="my-8" />
      
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Clock className="h-5 w-5 text-primary" />
          <h2 className="text-2xl font-bold">Mes demandes</h2>
        </div>

        {loadingTickets ? (
          <Card>
            <CardContent className="pt-6 text-center py-8">
              <p className="text-muted-foreground">Chargement...</p>
            </CardContent>
          </Card>
        ) : tickets.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center py-8">
              <p className="text-muted-foreground">
                Vous n'avez pas encore de demandes de support.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>
                {tickets.length} demande{tickets.length > 1 ? 's' : ''}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sujet</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Priorité</TableHead>
                    <TableHead>Créé le</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.map((ticket) => (
                    <TableRow 
                      key={ticket.id} 
                      data-testid={`ticket-row-${ticket.id}`}
                      className={ticket.status === 'awaiting_user' ? 'bg-destructive/5' : ''}
                    >
                      <TableCell className="font-medium">{ticket.subject}</TableCell>
                      <TableCell>{getStatusBadge(ticket.status)}</TableCell>
                      <TableCell>{getPriorityBadge(ticket.priority)}</TableCell>
                      <TableCell>
                        {ticket.created_at ? format(new Date(ticket.created_at), 'dd/MM/yyyy', { locale: fr }) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/support/tickets/${ticket.id}`)}
                          data-testid={`view-ticket-${ticket.id}`}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Voir
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
